import React from 'react';
import moment from 'moment-timezone';
import { graphql } from 'react-apollo';
import gql from 'graphql-tag';
import { useQuery } from '@apollo/react-hooks';
import { utils } from 'kb-shared';
import { Appointment } from 'kb-shared';
import { DailyInstruction } from 'kb-shared/types';

import CycleAppointment from './CycleAppointment';
import SubstepWrapper from '../SubstepWrapper';
const { inPast, inFuture } = utils;

export const CYCLE_VISITS = gql`
  query cycleVisits {
    cycleVisitDays

    cycleVisits {
      id
      location {
        id
        address
        name
        timeZone
      }
      startTime
      endTime
      timeZone
      cancelable
      reschedulable
    }
  }
`;

const dailyInstructionsQuery = gql`
  query DailyInstructions {
    dailyInstructions(all: true) {
      id
      drugs {
        id
        name
        amount
        timeOfDay
        dailyRecurrence
      }
      instructions
      date
    }
  }
`;

type DailyInstructionsResponse = {
  dailyInstructions: DailyInstruction[];
};

interface AppointmentsListProps {
  appointments: Array<Appointment>;
  dates: Array<string>;
}

const AppointmentsList = ({ appointments, dates }: AppointmentsListProps) => {
  const { data, loading } = useQuery<DailyInstructionsResponse>(dailyInstructionsQuery);
  if ((!appointments && !dates) || loading) {
    return null;
  }

  const mostRecentAppointment = appointments
    .slice()
    .reverse()
    .find(appt => inPast(appt.startTime));

  const scheduledAppointments = appointments.map((appointment, idx) =>
    // locationDetails has to be specified otherwise location prop gets overwritten by router location
    ({
      date: appointment.startTime,
      component: (
        <CycleAppointment
          {...appointment}
          idx={idx}
          key={appointment.id}
          locationDetails={appointment.location}
          showResults={
            (mostRecentAppointment && appointment.id === mostRecentAppointment.id) || false
          }
        />
      )
    })
  );

  if (dates && dates.length > 0) {
    const remainingCycleVisits = dates.filter(
      date =>
        inFuture(date) &&
        !appointments.some(appt => moment(appt.startTime).isSame(moment(date), 'day'))
    );
    const unscheduledCycleVisits = remainingCycleVisits.map((date, idx) => ({
      date,
      component: <CycleAppointment idx={idx + appointments.length} key={date} />
    }));
    const allAppointments = scheduledAppointments.concat(unscheduledCycleVisits);

    return (
      <SubstepWrapper
        substeps={allAppointments}
        dailyInstructions={data?.dailyInstructions || []}
      />
    );
  } else return null;
};

const withData = graphql(CYCLE_VISITS, {
  props: ({ data }) => ({
    // @ts-ignore
    appointments: data && data.cycleVisits,
    // @ts-ignore
    dates: data && data.cycleVisitDays
  })
});

// @ts-ignore
export default withData(AppointmentsList);
